import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import { ProtectedRoute, SubscriptionGate } from './utils/AuthContext.jsx';
import { SuperuserProvider } from './utils/admin/SuperuserContext';
import MaintenanceGuard from './components/MaintenanceGuard';

// Lazy loading de páginas para otimizar bundle size
const HomeLanding = lazy(() => import('./pages/home-landing'));
const UserLogin = lazy(() => import('./pages/user-login'));
const ChatInterface = lazy(() => import('./pages/chat-interface'));
const UserDashboard = lazy(() => import('./pages/user-dashboard'));
const UserRegistration = lazy(() => import('./pages/user-registration'));
const ConversationHistory = lazy(() => import('./pages/conversation-history'));
const ConversationView = lazy(() => import('./pages/conversation-history/view'));
const UserSettings = lazy(() => import('./pages/user-dashboard/components/UserSettings'));
const PricingPage = lazy(() => import('./pages/pricing'));
const VerifyEmailPage = lazy(() => import('./pages/verify-email'));
const CheckoutSuccess = lazy(() => import('./pages/checkout-success'));
const ForgotPassword = lazy(() => import('./pages/forgot-password'));
const ResetPassword = lazy(() => import('./pages/reset-password'));
const AdminPanel = lazy(() => import('./pages/admin'));
const MaintenancePage = lazy(() => import('./pages/maintainance'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Componente de loading para Suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="text-muted-foreground text-sm">Carregando...</span>
    </div>
  </div>
);

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
      <RouterRoutes>
        {/* Define your route here */}
        <Route path="/" element={
          <MaintenanceGuard route="/">
            <HomeLanding />
          </MaintenanceGuard>
        } />
        <Route path="/user-login" element={
          <MaintenanceGuard route="/user-login">
            <UserLogin />
          </MaintenanceGuard>
        } />
        <Route path="/home-landing" element={<HomeLanding />} />
        <Route path="/chat-interface" element={
          <MaintenanceGuard route="/chat-interface">
            <ProtectedRoute>
              <SubscriptionGate>
                <ChatInterface />
              </SubscriptionGate>
            </ProtectedRoute>
          </MaintenanceGuard>
        } />
        <Route path="/user-dashboard" element={
          <MaintenanceGuard route="/user-dashboard">
            <ProtectedRoute>
              <SubscriptionGate>
                <UserDashboard />
              </SubscriptionGate>
            </ProtectedRoute>
          </MaintenanceGuard>
        } />
        <Route path="/user-registration" element={
          <MaintenanceGuard route="/user-registration">
            <UserRegistration />
          </MaintenanceGuard>
        } />
        <Route path="/conversation-history" element={
          <MaintenanceGuard route="/conversation-history">
            <ProtectedRoute>
              <SubscriptionGate>
                <ConversationHistory />
              </SubscriptionGate>
            </ProtectedRoute>
          </MaintenanceGuard>
        } />
        <Route path="/conversation/:id" element={
          <MaintenanceGuard route="/conversation-history">
            <ProtectedRoute>
              <SubscriptionGate>
                <ConversationView />
              </SubscriptionGate>
            </ProtectedRoute>
          </MaintenanceGuard>
        } />
        <Route path="/user-settings" element={
          <MaintenanceGuard route="/user-settings">
            <UserSettings />
          </MaintenanceGuard>
        } />
        <Route path="/pricing" element={
          <MaintenanceGuard route="/pricing">
            <PricingPage />
          </MaintenanceGuard>
        } />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={
          <MaintenanceGuard route="/forgot-password">
            <ForgotPassword />
          </MaintenanceGuard>
        } />
        <Route path="/reset-password" element={
          <MaintenanceGuard route="/reset-password">
            <ResetPassword />
          </MaintenanceGuard>
        } />
        <Route path="/checkout-success" element={
          <ProtectedRoute>
            <CheckoutSuccess />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <SuperuserProvider>
            <AdminPanel />
          </SuperuserProvider>
        } />
        <Route path="/maintainance" element={
          <SuperuserProvider>
            <MaintenancePage />
          </SuperuserProvider>
        } />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
